import React from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { Button, Carousel, CarouselItem } from '@americanexpress/dls-react';
import { Helmet } from 'react-helmet';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { useRouteParams } from '@/context/routeParamsContext';
import { getLocaleData } from '@/selectors/localeSelectors';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { formatText } from '@/helpers/utils';
import { useAnalyticsContext } from '@/context/analyticsContext';
import LFLink from '../common/LFLink';
import AirportsSearchBox from '../common/AirportsSearchBox';
import LandingPartnerLogosSection from './LandingPartnerLogosSection';
import styles from './LandingPage.module.scss';
import CardCalloutNotification from '../common/CardCalloutNotification';
import ChevronLeft from '../icons/ChevronLeft';
import touchTargetStyles from '../common/TargetTouchSize.module.scss';

const LandingPage = () => {
  const { customTrack } = useAnalyticsContext();
  const nextPrevCarouselArrowSelector =
    'button[aria-controls="home-page-carousel-items"]';
  const cmsImgBaseUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const { product } = useRouteParams();
  const { locale } = useSelector(getLocaleData);
  const {
    pageTitles: { landing },
    featuredAirports,
    carousel,
    productDisplayNames,
  } = useCommonContent();
  const isUsMarket = !locale || locale.toLocaleLowerCase() === 'en-us';

  const modifiedCarousel = carousel.some((item) =>
    item.supportedCards?.includes(product)
  )
    ? carousel.filter((item) => item.supportedCards?.includes(product))
    : carousel;

  // for meta description
  const productName = productDisplayNames[product]?.displayName?.replace(
    'The ',
    ''
  );

  const handleCarouselArrowClick = (event) => {
    event.stopPropagation();
    const arrow = event.target.closest(nextPrevCarouselArrowSelector);
    if (arrow) {
      customTrack({
        event,
        element: `HomePage Carousel Arrow`,
      });
    }
  };

  const clickHandler = (event, element) => {
    customTrack({
      event,
      element: `${element} Carousel Button`,
    });
  };

  return (
    <div className="dls-white-bg">
      <Helmet>
        <title>{landing?.title}</title>
        <meta
          name="description"
          content={formatText(landing?.description, { productName })}
        />
      </Helmet>

      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
        <div className={styles.heroGrayBackground} />
        <Carousel
          id="home-page"
          isNavigationOnSlides={true}
          className={styles.heroCarousel}
          onClick={handleCarouselArrowClick}
          reactlytics={{ disable: true }}
          data-testid="carousel"
        >
          {modifiedCarousel.map((item) => (
            <CarouselItem
              id={item.title}
              key={item.title}
              className={styles.heroCarousel}
            >
              <div
                className={styles.heroImageContainer}
                style={{
                  backgroundImage: `url(${cmsImgBaseUrl}${item.img})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                {/* darker gradient required for the HND Centurion Lounge image */}
                <div
                  className={
                    item.img === 'hnd-centurion-lounge.jpg'
                      ? styles.darkHeroImageGradient
                      : styles.heroImageGradient
                  }
                />
                <div className={styles.heroImageContent}>
                  <h1 className={styles.imageTitle}>{item.title}</h1>
                  <p className={styles.imageDescription}>{item.description}</p>
                  {item.url ? (
                    <a
                      tabIndex={-1}
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button
                        className={styles.heroCtaButton}
                        onClick={(e) => clickHandler(e, item.cta)}
                        reactlytics={{ disable: true }}
                      >
                        {item.cta}
                      </Button>
                    </a>
                  ) : (
                    <Button
                      className={styles.heroCtaButton}
                      onClick={(e) => {
                        clickHandler(e, item.cta);
                        const searchBar =
                          document.querySelector('#findLounges');
                        if (searchBar) {
                          searchBar.focus();
                        }
                      }}
                      reactlytics={{ disable: true }}
                    >
                      {item.cta}
                    </Button>
                  )}
                </div>
              </div>
            </CarouselItem>
          ))}
        </Carousel>
        <div className={styles.searchAndCalloutWrapper}>
          <div>
            <AirportsSearchBox className={styles.searchWrapper} />
            <CardCalloutNotification className={styles.cardCalloutLanding} />
          </div>
        </div>
      </div>
      <div className={styles.partnerAndTermsWrapper}>
        <h3 className="tw-heading-md-regular tw-mt-10 tw-text-gray-900">
          {featuredAirports?.header}
        </h3>
        <ul className="tw-my-6 tw-grid tw-gap-6 sm_tw-grid-cols-2 lg_tw-grid-cols-3">
          {featuredAirports?.airports.map((airport) => {
            const urlAirportIata = airport.iata ? `/${airport.iata}` : '';
            return (
              <li key={airport.iata}>
                <LFLink
                  key={`featured-airports-link-${airport.iata}`}
                  element="featured-airports-link"
                  className="tw-flex"
                  to={`${product}${urlAirportIata}/`}
                >
                  <img
                    alt=""
                    src={`${cmsImgBaseUrl}${airport.image}`}
                    className="tw-h-12 tw-w-12 tw-rounded-lg tw-bg-gray-300"
                  />
                  <div className="tw-ml-4 tw-flex tw-flex-col">
                    <span className="tw-text-gray-800">{airport.name}</span>
                    <span className="tw-text-xs tw-text-gray-700">
                      {airport.iata} • {airport.city}
                    </span>
                  </div>
                </LFLink>
              </li>
            );
          })}
        </ul>
        {isUsMarket && (
          <LFLink
            to={`${product}/all-airports/`}
            className={clsx(
              'tw-my-2dot5 tw-inline-flex tw-items-center tw-font-medium tw-text-link',
              touchTargetStyles.touchTarget
            )}
            element="all-airports-link"
          >
            {featuredAirports?.link}
            <ChevronLeft className="tw-ml-1 tw-inline tw-h-3 tw-w-3 tw-rotate-180" />
          </LFLink>
        )}
        <LandingPartnerLogosSection />
      </div>
    </div>
  );
};

export default LandingPage;
